import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  getKnowledgeAssetsUser,
  getUpcDbmlAssets,
  buildAssetPhaseMap,
  type KnowledgeAsset,
} from '@/services/workspace'
import type { FileNode, FileCategory, ProcessingPhase } from '@/types'

export interface WorkspaceAssetsState {
  files: FileNode[]
  upcFiles: FileNode[]
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
  /** projectName for which assets are currently loaded */
  loadedFor: string | null
}

const initialState: WorkspaceAssetsState = {
  files: [],
  upcFiles: [],
  status: 'idle',
  error: null,
  loadedFor: null,
}

// Map backend asset type string → FileCategory
function mapTypeToCategory(type: string): FileCategory {
  const t = (type ?? '').toUpperCase()
  if (['SAS', 'QVS', 'QVS', 'PYTHON', 'JAVA', 'SQL', 'GENERAL'].includes(t))
    return 'Code'
  if (['DATABASE', 'DB', 'MYSQL', 'POSTGRES', 'ORACLE', 'SNOWFLAKE'].includes(t))
    return 'Database'
  if (['DOC', 'DOCS', 'PDF', 'MARKDOWN', 'MD'].includes(t)) return 'Docs'
  if (['CONFIG', 'YAML', 'JSON', 'ENV', 'XML'].includes(t)) return 'Config'
  if (['TEST', 'SPEC'].includes(t)) return 'Test'
  return 'Other'
}

// Map highest phase string → ProcessingPhase label used by the UI
// pseudocode          → 'Blueprint'   (initial pseudocode generated)
// source_pseudocode   → 'Build'       (blueprint finalised, ready for Build tab)
// target_code         → 'Validation'  (target code generated, ready for Validation tab)
// test_cases/testcases→ 'Export'      (test cases done, ready for Export tab)
function mapPhaseToUI(phase: string): ProcessingPhase | undefined {
  const p = (phase ?? '').toLowerCase()
  if (p === 'pseudocode') return 'Blueprint'
  if (p === 'source_pseudocode') return 'Build'
  if (p === 'target_code') return 'Validation'
  if (p === 'test_cases' || p === 'testcases') return 'Export'
  return undefined
}

// UPC asset IDs often carry suffixes like _Developer, _Business that are absent
// from the knowledge asset IDs. Build an extended map that also indexes by base ID.
// Priority when multiple UPC entries resolve to the same base ID (highest wins):
//   test_cases/testcases > target_code > source_pseudocode > pseudocode
const UPC_SUFFIXES = ['_Developer', '_Business', '_source_pseudocode', '_target_code', '_test_cases']
const UPC_PHASE_PRIORITY = ['pseudocode', 'source_pseudocode', 'target_code', 'test_cases', 'testcases']

function buildExtendedPhaseMap(phaseMap: Record<string, string>): Record<string, string> {
  const extended = { ...phaseMap }
  Object.entries(phaseMap).forEach(([id, phase]) => {
    for (const suffix of UPC_SUFFIXES) {
      if (id.endsWith(suffix)) {
        const baseId = id.slice(0, id.length - suffix.length)
        if (!(baseId in extended)) {
          extended[baseId] = phase
        } else {
          // Keep whichever phase is more advanced (Export > Validation > Build > Blueprint)
          const existingPri = UPC_PHASE_PRIORITY.indexOf(extended[baseId])
          const newPri = UPC_PHASE_PRIORITY.indexOf(phase)
          if (newPri > existingPri) extended[baseId] = phase
        }
      }
    }
  })
  return extended
}

/**
 * Build a single FileNode from a named tree node.
 */
function buildZipNode(
  node: any,
  phaseMap: Record<string, string>
): FileNode | null {
  if (!node) return null

  // Primary backend format: { name, type:"file"|"folder", asset_id?, children? }
  if (typeof node.type === 'string') {
    if (node.type === 'file' && node.asset_id) {
      const id: string = node.asset_id
      const phase = phaseMap[id] ? mapPhaseToUI(phaseMap[id]) : 'Discovery'
      return { id, name: node.name ?? id, nodeType: 'file', fileCategory: 'Code', phase }
    }
    if (node.type === 'folder') {
      const children: FileNode[] = (node.children ?? [])
        .map((c: any) => buildZipNode(c, phaseMap))
        .filter((n: FileNode | null): n is FileNode => n !== null)
      const id: string = node.asset_id ?? `folder_${node.name}`
      if (children.length === 0) return null
      return { id, name: node.name ?? 'folder', nodeType: 'folder', children }
    }
    return null
  }

  // Legacy: plain asset_id string
  if (typeof node === 'string') {
    const phase = phaseMap[node] ? mapPhaseToUI(phaseMap[node]) : 'Discovery'
    return { id: node, name: node, nodeType: 'file', fileCategory: 'Code', phase }
  }

  // Legacy: object with asset_id
  if (node.asset_id) {
    const id: string = node.asset_id
    const phase = phaseMap[id] ? mapPhaseToUI(phaseMap[id]) : 'Discovery'
    return { id, name: node.file_name ?? node.name ?? id, nodeType: 'file', fileCategory: 'Code', phase }
  }

  return null
}

/**
 * Extract FileNode children from a ZIP asset's tree.
 * The backend wraps the whole tree in a root folder node whose asset_id is the ZIP itself.
 * We skip that root and return its children directly.
 */
function buildZipChildren(
  tree: Record<string, any> | Array<any>,
  phaseMap: Record<string, string>
): FileNode[] {
  if (!tree) return []

  // Named-node root folder → use its children directly (root = the ZIP itself)
  if (!Array.isArray(tree) && typeof (tree as any).type === 'string') {
    const root = tree as any
    if (root.type === 'folder' && Array.isArray(root.children)) {
      return root.children
        .map((c: any) => buildZipNode(c, phaseMap))
        .filter((n: FileNode | null): n is FileNode => n !== null)
    }
    const single = buildZipNode(root, phaseMap)
    return single ? [single] : []
  }

  // Array of nodes
  if (Array.isArray(tree)) {
    return tree
      .map((c: any) => buildZipNode(c, phaseMap))
      .filter((n: FileNode | null): n is FileNode => n !== null)
  }

  // Legacy dict format: { filename: asset_id_string | {asset_id, ...} }
  const nodes: FileNode[] = []
  Object.entries(tree).forEach(([key, val]) => {
    if (typeof val === 'string') {
      const phase = phaseMap[val] ? mapPhaseToUI(phaseMap[val]) : 'Discovery'
      nodes.push({ id: val, name: key, nodeType: 'file', fileCategory: 'Code', phase })
    } else if (val && typeof val === 'object') {
      if ((val as any).asset_id) {
        const id: string = (val as any).asset_id
        const phase = phaseMap[id] ? mapPhaseToUI(phaseMap[id]) : 'Discovery'
        nodes.push({ id, name: key, nodeType: 'file', fileCategory: 'Code', phase })
      } else {
        const children = buildZipChildren(val as Record<string, any>, phaseMap)
        if (children.length > 0)
          nodes.push({ id: `folder_${key}`, name: key, nodeType: 'folder', children })
      }
    }
  })
  return nodes
}

export const fetchWorkspaceAssets = createAsyncThunk(
  'workspaceAssets/fetch',
  async (
    arg: { projectId?: string; projectName: string; phase?: string } | string,
    { rejectWithValue }
  ) => {
    const projectName = typeof arg === 'string' ? arg : arg.projectName
    const projectId = typeof arg === 'string' ? undefined : arg.projectId
    const phase = typeof arg === 'string' ? undefined : arg.phase

    const [knowledgeRes, upcRes] = await Promise.all([
      getKnowledgeAssetsUser(),
      getUpcDbmlAssets(projectId, phase),
    ])

    if (!knowledgeRes.success) {
      return rejectWithValue(knowledgeRes.message ?? 'Failed to load assets')
    }

    const allKnowledge: KnowledgeAsset[] =
      (knowledgeRes.data as any)?.mongo_knowledge_assets ?? []

    // Filter by project and exclude markdown helper assets
    const projectAssets = allKnowledge.filter(
      (a) =>
        a.project_name === projectName && (a.type ?? '').toLowerCase() !== 'markdown'
    )

    // Build phase map from UPC assets
    const allUpc: any[] = (upcRes.data as any)?.mongo_knowledge_assets ?? []

    // Per user request, remove filtering block and assume all upc assets belong to current view
    const projectUpc = allUpc

    const phaseMap = buildAssetPhaseMap(projectUpc)
    const extendedPhaseMap = buildExtendedPhaseMap(phaseMap)

    // IDs of all ZIP (folder) assets — used to detect flat files that belong
    // inside a ZIP. Those are already represented inside the ZIP's tree, so we
    // exclude them from the top-level list to avoid duplication.
    const zipAssetIds = new Set(
      projectAssets.filter((a) => a.is_zip).map((a) => a.asset_id)
    )


    // Prepare upcFiles directly from UPC assets (1 per file asset_id)
    // Always use asset_id (not parent_asset_id) as the node ID so it matches
    // the KA asset_id used in Discovery and the /download, /preview_all_pseudocodes APIs.
    const uniqueUpcAssets = new Map<string, any>()
    projectUpc.forEach((asset) => {
      const id = asset.asset_id
      if (!id) return
      if (!uniqueUpcAssets.has(id)) {
        uniqueUpcAssets.set(id, asset)
      } else {
        const existing = uniqueUpcAssets.get(id)
        if (!existing.document_name && asset.document_name) {
          uniqueUpcAssets.set(id, asset)
        }
      }
    })

    const upcFiles: FileNode[] = Array.from(uniqueUpcAssets.values()).map((asset) => {
      const highestPhase = phaseMap[asset.asset_id]
      const isDbMigration = ['database', 'dbml'].includes((asset.document_type ?? '').toLowerCase())
      return {
        id: asset.asset_id,
        name: asset.document_name ?? asset.asset_id,
        nodeType: 'file' as const,
        fileCategory: 'Code' as const,
        status: 'Analyzed' as const,
        phase: highestPhase ? mapPhaseToUI(highestPhase) : undefined,
        ...(isDbMigration ? { dbType: asset.document_type } : {}),
      }
    })

    // Map to FileNode[] for sidebar (Discovery and project-tree)
    // - is_zip=true  → folder node with hierarchical children from tree
    // - is_zip=false AND parent_asset_id points to a ZIP → skip (already in tree)
    // - is_zip=false AND no ZIP parent → flat file at top level
    const files: FileNode[] = projectAssets
      .filter((asset) => {
        if (asset.is_zip) return true
        // Exclude flat files whose parent is a ZIP — they live inside the folder tree
        return !asset.parent_asset_id || !zipAssetIds.has(asset.parent_asset_id)
      })
      .map((asset) => {
        if (asset.is_zip && asset.tree) {
          return {
            id: asset.asset_id,
            name: asset.file_name ?? asset.asset_name,
            nodeType: 'folder' as const,
            children: buildZipChildren(asset.tree, extendedPhaseMap),
          }
        }
        const highestPhase = extendedPhaseMap[asset.asset_id]
        return {
          id: asset.asset_id,
          name: asset.file_name ?? asset.asset_name,
          nodeType: 'file' as const,
          fileCategory: mapTypeToCategory(asset.type),
          status: 'Analyzed' as const,
          phase: highestPhase ? mapPhaseToUI(highestPhase) : 'Discovery',
          ...(asset.db_type ? { dbType: asset.db_type } : {}),
        }
      })

    return { files, upcFiles, projectName }
  }
)

const workspaceAssetsSlice = createSlice({
  name: 'workspaceAssets',
  initialState,
  reducers: {
    resetWorkspaceAssets: (state) => {
      state.files = []
      state.status = 'idle'
      state.error = null
      state.loadedFor = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspaceAssets.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchWorkspaceAssets.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.files = action.payload.files
        state.upcFiles = action.payload.upcFiles
        state.loadedFor = action.payload.projectName
      })
      .addCase(fetchWorkspaceAssets.rejected, (state, action) => {
        state.status = 'failed'
        state.error = (action.payload as string) ?? 'Failed to load assets'
      })
  },
})

export const { resetWorkspaceAssets } = workspaceAssetsSlice.actions
export default workspaceAssetsSlice.reducer
